import * as React from "react";

import {
  ActionScopes,
  Auth0Permissions,
  ChartScopes,
  ModulesScopes,
} from "types";

import { Buffer } from "buffer";
import { useAuth0 } from "@auth0/auth0-react";

type PermissionsContextValue = {
  isLoadingPermissions: boolean;
  scope(s: ModulesScopes | ActionScopes | ChartScopes): {
    isAccessible: boolean;
    isEditable: boolean;
  };
  /**
   * Raw membership check against the JWT `permissions` claim. Unlike
   * `scope().isEditable`, this is NOT gated by the `auditChartReadOnly` session
   * flag, so it reflects the permission the user actually holds regardless of the
   * current chart's read-only state.
   */
  hasPermission(permission: Auth0Permissions): boolean;
  /**
   * Canonical auditor check: true if the user holds the seeded `audits:auditor`
   * permission or any future section-scoped `*:auditor` permission.
   */
  isAuditor: boolean;
  claims: Record<string, unknown>;
  /**
   * True when the audits flow has locked the current chart read-only.
   *
   * This is the reactive view of the `auditChartReadOnly` session flag. Read it
   * instead of `sessionStorage` directly: a `sessionStorage` write triggers no
   * re-render, so consumers that read the raw flag during render keep whatever
   * value was current when they last rendered and stay stuck on it when the
   * user switches charts.
   */
  isAuditChartReadOnly: boolean;
  /**
   * Publishes the audits read-only lock. Pass `null` to clear the flag back to
   * unset (distinct from `false`, which explicitly marks the chart editable —
   * see the `isAuditChartEditable` note in `scope`). Owned by `ChartsSidebar`.
   */
  setAuditChartReadOnly(value: boolean | null): void;
  /**
   * The clinical family whose chart sections a commenter may temporarily edit
   * while their audit review turn is active. `null` when the current user is not
   * a reviewing commenter or their role does not match the current chart type.
   *
   * This is reactive context state (no sessionStorage mirror). ChartTracking
   * republishes it after refetch on refresh.
   */
  commenterChartEditFamily: "Provider" | "Nurse" | null;
  /**
   * Publishes the commenter chart-edit family. Pass `null` to clear the
   * elevation. Owned by `ChartTrackingContextProvider`.
   */
  setCommenterChartEditFamily(value: "Provider" | "Nurse" | null): void;
};

const PermissionsContext = React.createContext<PermissionsContextValue | null>(
  null
);

// allowlist by design (advisor recommendation) — new provider: scopes do NOT
// become commenter-editable automatically.
const COMMENTER_EDITABLE_PROVIDER_SCOPES: ReadonlySet<string> = new Set([
  "provider:patientinfo",
  "provider:vitals",
  "provider:hpi",
  "provider:mh",
  "provider:ros",
  "provider:pex",
  "provider:ci",
  "provider:mdm",
  "provider:notes",
  "provider:ai",
  "provider:proc",
  "provider:sup",
  "provider:treatments",
  "provider:rad",
  "provider:labs",
  "provider:disp",
]);

// allowlist by design (advisor recommendation) — new nurse: scopes do NOT
// become commenter-editable automatically.
const COMMENTER_EDITABLE_NURSE_SCOPES: ReadonlySet<string> = new Set([
  "nurse:patientinfo",
  "nurse:vitals",
  "nurse:cc",
  "nurse:ai",
  "nurse:mh",
  "nurse:sup",
  "nurse:rad",
  "nurse:labs",
  "nurse:disp",
  "nurse:proc",
  "nurse:actions",
  "nurse:assts",
]);

const AUDIT_CHART_READ_ONLY_STORAGE_KEY = "auditChartReadOnly";

// Keep in sync with the backend auditor detection
// (Utils/Auth0Utils/ClaimsPrincipalUtils.IsAuditorPermission): the seeded
// auditor permission is "audits:auditor"; section-scoped "*:auditor"
// permissions are future-proofed here.
const isAuditorPermission = (permission: string) =>
  permission === "audits:auditor" || permission.endsWith(":auditor");

type AuditChartReadOnlyFlag = "true" | "false" | null;

/**
 * `sessionStorage` remains the backing store so the lock survives a reload, but
 * it is mirrored into React state so changes actually re-render consumers.
 */
function readAuditChartReadOnlyFlag(): AuditChartReadOnlyFlag {
  if (typeof window === "undefined") return null;

  const value = window.sessionStorage.getItem(
    AUDIT_CHART_READ_ONLY_STORAGE_KEY
  );

  return value === "true" || value === "false" ? value : null;
}

function PermissionsProvider(props: React.PropsWithChildren) {
  const { children } = props;
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();

  const [permissions, setPermissions] = React.useState<Auth0Permissions[]>([]);
  const [claims, setClaims] = React.useState<Record<string, unknown>>({});
  const [isGeneratingPermissions, setIsGeneratingPermissions] =
    React.useState<boolean>(true);
  const [auditChartReadOnlyFlag, setAuditChartReadOnlyFlag] =
    React.useState<AuditChartReadOnlyFlag>(readAuditChartReadOnlyFlag);
  const [commenterChartEditFamily, setCommenterChartEditFamily] =
    React.useState<"Provider" | "Nurse" | null>(null);

  const setAuditChartReadOnly = React.useCallback((value: boolean | null) => {
    const flag: AuditChartReadOnlyFlag =
      value === null ? null : value ? "true" : "false";

    if (typeof window !== "undefined") {
      if (flag === null) {
        window.sessionStorage.removeItem(AUDIT_CHART_READ_ONLY_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(AUDIT_CHART_READ_ONLY_STORAGE_KEY, flag);
      }
    }

    setAuditChartReadOnlyFlag(flag);
  }, []);

  const isLoadingPermissions = isLoading || isGeneratingPermissions;

  const isAuditor = React.useMemo(
    () => permissions.some(isAuditorPermission),
    [permissions]
  );

  const generatePermissions = React.useCallback(async () => {
    if (isLoading) return;
    try {
      const token = await getAccessTokenSilently();
      if (!isAuthenticated || !token) return;
      const tokenParts = token.split(".");
      const decrypted = JSON.parse(
        Buffer.from(
          tokenParts.length > 1 ? tokenParts[1] : "" || "",
          "base64"
        ).toString()
      );

      setPermissions(decrypted?.permissions);

      const decryptedClaims: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(decrypted)) {
        if (key.includes("claims"))
          decryptedClaims[key.split("/").pop() || ""] = value;
      }

      setClaims(decryptedClaims);
      setIsGeneratingPermissions(false);
    } catch {
      // getAccessTokenSilently can throw during initial load or when
      // consent is required; the effect re-runs when deps change
      setIsGeneratingPermissions(false);
    }
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  React.useEffect(() => {
    generatePermissions();
  }, [generatePermissions]);

  const scope: PermissionsContextValue["scope"] = React.useCallback(
    (s) => {
      const isChartScope =
        typeof s === "string" &&
        (s.startsWith("provider:") || s.startsWith("nurse:"));
      const isGlobalAuditor = permissions.some(isAuditorPermission);

      const isAuditChartReadOnly = auditChartReadOnlyFlag === "true";
      // Elevate the global auditor to editor only once the audits flow has
      // explicitly marked this chart editable ("false"). Requiring an explicit
      // "false" (rather than "not 'true'") keeps this opt-in: an unset/null flag
      // — e.g. a chart opened outside the audits flow before ChartsSidebar runs —
      // does not grant edit access.
      const isAuditChartEditable = auditChartReadOnlyFlag === "false";

      const hasEditPermission =
        permissions.includes(`${s}:edit` as Auth0Permissions) ||
        permissions.includes(`${s}:auditor` as Auth0Permissions) ||
        (isGlobalAuditor && isChartScope && isAuditChartEditable) ||
        // Commenters in their audit review turn may edit CHART-ANSWER sections
        // of their role's chart family only. Order history, discharge-summary
        // signing, progress notes, chart completion, and inprocess scopes are
        // intentionally excluded.
        (commenterChartEditFamily === "Provider" &&
          COMMENTER_EDITABLE_PROVIDER_SCOPES.has(s)) ||
        (commenterChartEditFamily === "Nurse" &&
          COMMENTER_EDITABLE_NURSE_SCOPES.has(s));

      return {
        isAccessible:
          permissions.includes(`${s}:view` as Auth0Permissions) ||
          permissions.includes(`${s}:edit` as Auth0Permissions) ||
          permissions.includes(`${s}:commenter` as Auth0Permissions) ||
          permissions.includes(`${s}:auditor` as Auth0Permissions) ||
          permissions.includes(s as Auth0Permissions) ||
          permissions.includes(`${s}:adddelete` as Auth0Permissions) ||
          permissions.includes(`${s}:add` as Auth0Permissions) ||
          permissions.includes(`${s}:dellete` as Auth0Permissions),
        // A non-null commenterChartEditFamily already proves the commenter review turn
        // (InReview + role match), so a stale auditChartReadOnly flag must not suppress
        // the elevation.
        isEditable:
          hasEditPermission &&
          (!isAuditChartReadOnly || commenterChartEditFamily !== null),
      };
    },
    [permissions, auditChartReadOnlyFlag, commenterChartEditFamily]
  );

  const hasPermission = React.useCallback(
    (permission: Auth0Permissions) => permissions.includes(permission),
    [permissions]
  );

  const value: PermissionsContextValue = React.useMemo(
    () => ({
      isLoadingPermissions,
      scope,
      hasPermission,
      isAuditor,
      claims,
      isAuditChartReadOnly: auditChartReadOnlyFlag === "true",
      setAuditChartReadOnly,
      commenterChartEditFamily,
      setCommenterChartEditFamily,
    }),
    [
      isLoadingPermissions,
      scope,
      hasPermission,
      isAuditor,
      claims,
      auditChartReadOnlyFlag,
      setAuditChartReadOnly,
      commenterChartEditFamily,
      setCommenterChartEditFamily,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

function usePermissions() {
  const context = React.useContext(PermissionsContext);

  if (context === null) {
    throw new Error(
      "usePermissions must be used within an PermissionsProvider"
    );
  }

  return context;
}

/**
 * Single source of truth for the audits-flow roles derived from the `audits`
 * scope:
 * - `isAuditor`: can currently edit the audit — `audits:auditor` AND not gated
 *   read-only by the `auditChartReadOnly` session flag.
 * - `isCommenter`: holds the specific `audits:commenter` role. This is derived
 *   from raw permission membership rather than `!isEditable`, because a
 *   non-assigned auditor is flagged read-only (`isEditable === false`) yet is
 *   NOT a commenter. It also deliberately does NOT include other read-only
 *   audits permissions (`audits:view`, `audits:add`, etc.) — only the commenter
 *   role should get the commenter comment affordances.
 * - `hasAuditorRole`: holds any `*:auditor` permission, ungated by the read-only flag.
 *
 * Use `hasAuditorRole`, never `isAuditor`, in any expression that helps DECIDE
 * the read-only lock (e.g. "is this an auditor who isn't assigned to this
 * chart?"). `isAuditor` is gated by the flag, so feeding it back into the lock
 * creates a render cycle: locking flips `isAuditor` false, which unflags the
 * lock, which flips `isAuditor` back true. `isAuditor` is for CONSUMING the
 * lock ("may this user edit right now?"); role identity is not lock-dependent.
 */
function useAuditPermissions() {
  const { scope, hasPermission, isAuditor } = usePermissions();
  const auditsScope = scope("audits");

  return {
    isAuditor: auditsScope.isEditable,
    // Holding `audits:commenter` already implies `auditsScope.isAccessible`
    // (see scope()), so the raw membership check alone is sufficient.
    isCommenter: hasPermission("audits:commenter"),
    // Canonical ungated auditor check: any `*:auditor` permission, not just
    // the seeded `audits:auditor`. Kept ungated so it can safely decide the
    // read-only lock without creating a render cycle.
    hasAuditorRole: isAuditor,
  };
}

/**
 * Reactive accessor for the audits read-only lock. Chart surfaces must use this
 * rather than reading the `auditChartReadOnly` session flag during render — the
 * raw flag does not re-render on change, which left sections and audit-comment
 * icons locked after switching between a patient's nurse and provider charts.
 */
function useAuditChartReadOnly() {
  const { isAuditChartReadOnly, setAuditChartReadOnly } = usePermissions();

  return { isAuditChartReadOnly, setAuditChartReadOnly };
}

/**
 * Reactive accessor for the commenter chart-edit family elevation.
 * Owned by `ChartTrackingContextProvider`.
 */
function useCommenterChartEditFamily() {
  const { commenterChartEditFamily, setCommenterChartEditFamily } =
    usePermissions();

  return { commenterChartEditFamily, setCommenterChartEditFamily };
}

export {
  PermissionsProvider,
  usePermissions,
  useAuditPermissions,
  useAuditChartReadOnly,
  useCommenterChartEditFamily,
};
